import { create } from 'zustand'
import type { JournalEntryLine } from '@/services/accounting'
import type { HomebasePayrollRun, HomebasePullResponse } from '@/services/homebasePayroll'

export const HOMEBASE_PAYROLL_CACHE_KEY = 'bookkeeper:homebase-payroll-cache'
export const HOMEBASE_PAYROLL_CACHE_TTL_MS = 60 * 60 * 1000

export type WizardStep = 1 | 2 | 3 | 4

type CachedPayload = {
  savedAt: number
  step: WizardStep
  startDate: string
  endDate: string
  runs: HomebasePayrollRun[]
  selectedRunId: string | null
  pullData: HomebasePullResponse | null
  draftId: number | null
  draftStatus: string
  docNumber: string
  qboUrl: string | null
  lines: JournalEntryLine[]
  totalDebits: number | string
  totalCredits: number | string
  warnings: string[]
}

interface HomebasePayrollState {
  step: WizardStep
  startDate: string
  endDate: string
  runs: HomebasePayrollRun[]
  selectedRunId: string | null
  pullData: HomebasePullResponse | null
  draftId: number | null
  draftStatus: string
  docNumber: string
  qboUrl: string | null
  lines: JournalEntryLine[]
  totalDebits: number | string
  totalCredits: number | string
  warnings: string[]
  cachedAt: number | null

  setStep: (step: WizardStep) => void
  setStartDate: (startDate: string) => void
  setEndDate: (endDate: string) => void
  setRuns: (runs: HomebasePayrollRun[]) => void
  setSelectedRunId: (runId: string | null) => void
  setPullData: (pullData: HomebasePullResponse | null) => void
  setDraft: (patch: {
    draftId: number | null
    draftStatus?: string
    docNumber?: string
    qboUrl?: string | null
    lines: JournalEntryLine[]
    totalDebits: number | string
    totalCredits: number | string
    warnings: string[]
  }) => void
  setDraftPosted: (patch: { draftStatus: string; qboUrl: string | null }) => void
  setLines: (lines: JournalEntryLine[]) => void
  setDraftTotals: (totalDebits: number | string, totalCredits: number | string) => void
  reset: () => void
  isCacheValid: () => boolean
  expireIfStale: () => boolean
}

function loadFromStorage(): CachedPayload | null {
  try {
    const raw = localStorage.getItem(HOMEBASE_PAYROLL_CACHE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as CachedPayload
    if (!parsed?.savedAt) return null
    if (Date.now() - parsed.savedAt >= HOMEBASE_PAYROLL_CACHE_TTL_MS) {
      localStorage.removeItem(HOMEBASE_PAYROLL_CACHE_KEY)
      return null
    }
    return parsed
  } catch {
    localStorage.removeItem(HOMEBASE_PAYROLL_CACHE_KEY)
    return null
  }
}

function saveToStorage(state: {
  cachedAt: number | null
  step: WizardStep
  startDate: string
  endDate: string
  runs: HomebasePayrollRun[]
  selectedRunId: string | null
  pullData: HomebasePullResponse | null
  draftId: number | null
  draftStatus: string
  docNumber: string
  qboUrl: string | null
  lines: JournalEntryLine[]
  totalDebits: number | string
  totalCredits: number | string
  warnings: string[]
}): void {
  if (state.cachedAt == null) return
  const payload: CachedPayload = {
    savedAt: state.cachedAt,
    step: state.step,
    startDate: state.startDate,
    endDate: state.endDate,
    runs: state.runs,
    selectedRunId: state.selectedRunId,
    pullData: state.pullData,
    draftId: state.draftId,
    draftStatus: state.draftStatus,
    docNumber: state.docNumber,
    qboUrl: state.qboUrl,
    lines: state.lines,
    totalDebits: state.totalDebits,
    totalCredits: state.totalCredits,
    warnings: state.warnings,
  }
  localStorage.setItem(HOMEBASE_PAYROLL_CACHE_KEY, JSON.stringify(payload))
}

function touchCache(state: HomebasePayrollState): HomebasePayrollState {
  const cachedAt = Date.now()
  const next = { ...state, cachedAt }
  saveToStorage(next)
  return next
}

const initialCache = loadFromStorage()

export const useHomebasePayrollStore = create<HomebasePayrollState>((set, get) => ({
  step: initialCache?.step ?? 1,
  startDate: initialCache?.startDate ?? '',
  endDate: initialCache?.endDate ?? '',
  runs: initialCache?.runs ?? [],
  selectedRunId: initialCache?.selectedRunId ?? null,
  pullData: initialCache?.pullData ?? null,
  draftId: initialCache?.draftId ?? null,
  draftStatus: initialCache?.draftStatus ?? 'draft',
  docNumber: initialCache?.docNumber ?? '',
  qboUrl: initialCache?.qboUrl ?? null,
  lines: initialCache?.lines ?? [],
  totalDebits: initialCache?.totalDebits ?? 0,
  totalCredits: initialCache?.totalCredits ?? 0,
  warnings: initialCache?.warnings ?? [],
  cachedAt: initialCache?.savedAt ?? null,

  setStep: (step) => set((state) => touchCache({ ...state, step })),

  setStartDate: (startDate) => set((state) => touchCache({ ...state, startDate })),

  setEndDate: (endDate) => set((state) => touchCache({ ...state, endDate })),

  setRuns: (runs) => set((state) => touchCache({ ...state, runs })),

  setSelectedRunId: (selectedRunId) => set((state) => touchCache({ ...state, selectedRunId })),

  setPullData: (pullData) => set((state) => touchCache({ ...state, pullData })),

  setDraft: (patch) => set((state) => touchCache({ ...state, ...patch })),

  setDraftPosted: (patch) => set((state) => touchCache({ ...state, ...patch })),

  setLines: (lines) => set((state) => touchCache({ ...state, lines })),

  setDraftTotals: (totalDebits, totalCredits) =>
    set((state) => touchCache({ ...state, totalDebits, totalCredits })),

  reset: () => {
    localStorage.removeItem(HOMEBASE_PAYROLL_CACHE_KEY)
    set({
      step: 1,
      startDate: '',
      endDate: '',
      runs: [],
      selectedRunId: null,
      pullData: null,
      draftId: null,
      draftStatus: 'draft',
      docNumber: '',
      qboUrl: null,
      lines: [],
      totalDebits: 0,
      totalCredits: 0,
      warnings: [],
      cachedAt: null,
    })
  },

  isCacheValid: () => {
    const { cachedAt } = get()
    if (cachedAt == null) return false
    return Date.now() - cachedAt < HOMEBASE_PAYROLL_CACHE_TTL_MS
  },

  expireIfStale: () => {
    if (get().isCacheValid()) return false
    get().reset()
    return true
  },
}))
